<script setup>
import { ref, computed, watch } from 'vue'

import {useConfig} from '@/stores/config'
const configStore = useConfig();

import {useBooking} from '@/stores/booking'
const bookingStore = useBooking();

import dateFn from '@/mixins/date'

import SmartDateSelector from '@/components/SmartDateSelector.vue';
import Steps from '@/components/Steps.vue';

import { ChevronDownIcon, TagIcon } from '@heroicons/vue/24/outline'

import { 
  kBlock,
} from 'konsta/vue';

// TODO optional_services

const checkin = computed(() => dateFn.get_date_object(bookingStore.checkin))
const checkout = computed(() => dateFn.get_date_object(bookingStore.checkout))
const isMultiDays = computed(() => !!bookingStore.service?.is_multi_days)

const hasDiscount = computed(() => (bookingStore.appointment?.promo_code))

const formatPrice = (value) => {
  const intPart = Math.trunc(value)
  const decPart = (value * 100).toFixed(0).toString().padStart(2, '0').slice(-2)
  return `${intPart}.${decPart} €`
}

const optional_services_total = computed(() => {
  const list = bookingStore.service_or_bundle?.optional_services
  if(!list) return 0

  return list
    .filter(os => os.checked)
    .reduce((sum, os) => sum + bookingStore.getOptionalServicePrice(os), 0)
})

const total_listino = computed(() =>
  (bookingStore.appointment?.total_service_with_tax ?? 0) + optional_services_total.value
)

</script>

<template>
  <k-block strong 
    :class="[
      'relative mt-0 mb-0 w-full',
      'bg-primary rounded-bl-3xl md:rounded-none overflow-hidden text-white'
    ]">
    <div class="overflow-hidden absolute left-0 right-0 bottom-0 h-44 bg-primary shadow-lg z-0">
      <img src="@/images/bg-header.svg" class="object-cover opacity-20">
    </div>
    
    <div class="relative z-10">

      <!-- account -->
      <div class="flex justify-between items-center">
        <div>
          <div class="text-sm mb-1">Prenota da:</div>
          <div class="text-2xl font-bold leading-none">{{ configStore.account.name }}</div>
          <div class="">
            {{ configStore.account.address }}, {{ configStore.account.address_city }} ({{ configStore.account.address_province }}) - {{ configStore.account.address_zip_code }}
          </div>
        </div>
        <!--ChevronDownIcon class="h-6 w-6"/--> <!-- todo: multi accounts -->
      </div>

      <!-- steps -->
      <div
        class="block md:hidden">
        <Steps/>
      </div>

      <div 
        v-if="configStore.config.booking_steps?.indexOf(bookingStore.step) > configStore.config.booking_steps?.indexOf('stepService')"
        class="mt-6 cursor-pointer"
        @click="bookingStore.goBackToStep('stepService')">
        <div class="text-sm">Hai selezionato:</div>
        <div class="text-lg font-bold leading-none">{{ bookingStore.service_name }}</div>
        <div v-if="bookingStore.service_team_users.length" class="">con {{ bookingStore.service_team_users.map(u => u.frontend_name).join(', ') }}</div>
      </div>

      <div 
        v-if="configStore.config.booking_steps?.indexOf(bookingStore.step) > configStore.config.booking_steps?.indexOf('booking')"
        class="mt-6">
        <div class="flex flex-col md:flex-row md:justify-between md:items-end">
          <div v-if="!isMultiDays" class="flex">
            <div class="flex flex-col items-center font-bold text-lg">
              <div 
                class="rounded border py-1 px-2 leading-none border-white">
                {{ dateFn.format_time(bookingStore.start) }}
              </div>
              <ChevronDownIcon class="w-4 h-4 my-0.5"/>
              <div class="rounded border py-1 px-2 leading-none border-white">
                {{ dateFn.format_time(bookingStore.end + 1) }}
              </div>
            </div>

            <div class="ml-3">
              <div>{{ dateFn.get_day_name(dateFn.get_week_day_from_date(bookingStore.date_object)) }}</div>
              <div class="flex">
                <div class="text-6xl font-bold leading-none">{{ bookingStore.day_formatted }}</div>
                <div class="ml-2">
                  <div class="text-lg lowercase">{{ dateFn.get_month_name(bookingStore.month-1) }}</div>
                  <div class="">{{ bookingStore.year }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="flex">

            <div class="ml-3 flex flex-col">
              <div class="text-xs uppercase opacity-65 tracking-wide mb-0.5">Check-in</div>
              <div>{{ dateFn.get_day_name(dateFn.get_week_day_from_date(checkin)) }}</div>
              <div class="flex">
                <div class="text-6xl font-bold leading-none">{{ checkin.getDate() }}</div>
                <div class="ml-2">
                  <div class="text-lg lowercase">{{ dateFn.get_month_name(checkin.getMonth()) }}</div>
                  <div class="">{{ checkin.getFullYear() }}</div>
                </div>
              </div>
              <div v-if="isMultiDays" class="mt-2 self-start text-xs bg-black/20 rounded-full px-2 py-0.5">
                {{ bookingStore.bnb_nights }} notti
              </div>
            </div>

            <div class="border-l border-white/30 ml-3.5 pl-3.5">
              <div class="text-xs uppercase opacity-65 tracking-wide mb-0.5">Check-out</div>
              <div>{{ dateFn.get_day_name(dateFn.get_week_day_from_date(checkout)) }}</div>
              <div class="flex">
                <div class="text-6xl font-bold leading-none">{{ checkout.getDate() }}</div>
                <div class="ml-2">
                  <div class="text-lg lowercase">{{ dateFn.get_month_name(checkout.getMonth()) }}</div>
                  <div class="">{{ checkout.getFullYear() }}</div>
                </div>
              </div>
            </div>
          </div>

          <div 
            v-if="bookingStore.service_or_bundle?.show_prices && bookingStore.price_with_tax != null"
            class="mt-4 pt-3 border-t border-white/30 md:mt-0 md:pt-0 md:border-t-0 md:text-right md:border-l md:pl-2 md:h-full md:border-white">
            <div 
              v-if="hasDiscount"
              class="text-sm font-normal line-through opacity-55 leading-none mb-0.5">
              {{ formatPrice(total_listino) }}
            </div>

            <div class="text-lg font-bold leading-none">
              <span class="text-4xl">{{ Math.trunc(bookingStore.price_with_tax) }}</span>
              <span>.{{ (bookingStore.price_with_tax * 100).toFixed(0).toString().padStart(2, '0').slice(-2) }} €</span>
            </div>

            <div 
              v-if="hasDiscount"
              class="mt-1.5 inline-flex items-center gap-1 bg-white/15 text-xs font-normal px-2 py-0.5 rounded-full">
              <TagIcon class="w-3 h-3"/>
              {{ bookingStore.appointment.promo_code }}
            </div>
          </div>
        </div>
      </div>

      <div 
        v-else-if="configStore.config.booking_steps?.indexOf(bookingStore.step) > configStore.config.booking_steps?.indexOf('stepDate') && configStore.config.layout === 'layout_standard'"
        class="mt-3 block md:hidden">
        <SmartDateSelector/>
      </div>


    </div>
  </k-block>
</template>